import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bike,
  Clock,
  MapPin,
  Package,
  ShieldCheck,
  Wallet,
  Zap,
  DollarSign,
  Users,
  CheckCircle2,
  ArrowRight,
  Phone,
  Mail,
  Building2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { HeroSection } from "@/components/hero-section";
import heroImage from "@/assets/delivery1.jpg";
import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const Route = createFileRoute("/")({
  component: Landing,
});

function Landing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.title = "24Boda — Fast, Safe Boda Boda Delivery in Kampala, Uganda";

    // Navbar scroll effect
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? "bg-background/80 shadow-[0_1px_3px_rgba(0,0,0,0.05)] backdrop-blur-lg"
            : "bg-transparent backdrop-blur-none"
        }`}
      >
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-6 lg:px-12">
          <div className="flex items-center">
            <img src="/logo.png" alt="24Boda" className="h-10 w-auto" />
          </div>
          <nav className="hidden items-center gap-8 text-[15px] font-medium text-foreground/80 md:flex">
            <a href="/" className="transition-colors hover:text-foreground">
              Home
            </a>
            <a href="/about" className="transition-colors hover:text-foreground">
              About
            </a>
            <a href="/blog" className="transition-colors hover:text-foreground">
              Blog
            </a>
            <a href="/faq" className="transition-colors hover:text-foreground">
              FAQ
            </a>
            <a href="/join-riders" className="transition-colors hover:text-foreground">
              Riders
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href="https://apps.apple.com/app/24boda"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-black/90 sm:flex"
            >
              <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] leading-none opacity-80">Download on the</div>
                <div className="text-sm font-semibold leading-tight">App Store</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.24boda"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-black/90"
            >
              <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
              </svg>
              <div className="text-left">
                <div className="text-[9px] leading-none opacity-80">GET IT ON</div>
                <div className="text-sm font-semibold leading-tight">Google Play</div>
              </div>
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <HeroSection
          headline="We Always Deliver."
          highlightedText="Fast. Safe. Reliable."
          subheadline="The largest boda boda delivery service in Kampala. Fixed prices, live tracking, verified riders, and proof of delivery — every single time."
          primaryCTA={{
            text: "Book a delivery",
            href: "#download",
          }}
          secondaryCTA={{
            text: "Become a rider",
            href: "/join-riders",
          }}
          image={{
            src: heroImage,
            alt: "24Boda delivery service in Kampala",
          }}
          showStats={false}
        />

        {/* Services Section */}
        <section id="services" className="border-y border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="text-center">
              <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground">
                Why choose 24Boda?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Everything you need from a delivery service — no surprises, no hassle.
              </p>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <ValueCard
                icon={Zap}
                title="Lightning Fast"
                description="Average 8-minute pickup time. Most deliveries completed within 30 minutes."
              />
              <ValueCard
                icon={DollarSign}
                title="Transparent Pricing"
                description="See the exact fare before you book. Fixed prices by distance, no hidden fees."
              />
              <ValueCard
                icon={ShieldCheck}
                title="Verified Riders"
                description="Every rider is ID-checked, trained, and approved. Insurance coverage on every trip."
              />
              <ValueCard
                icon={MapPin}
                title="Live Tracking"
                description="Track your package in real-time from pickup to delivery. Share tracking links with customers."
              />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="text-center">
              <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground">
                How 24Boda works
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Three simple steps from request to delivered. No phone-call haggling.
              </p>
            </div>
            <div className="mt-16 grid gap-8 md:grid-cols-3">
              <StepCard
                step="1"
                icon={Package}
                title="Tell us what to move"
                description="Enter pickup and drop-off locations, choose package size, and see your fare instantly."
              />
              <StepCard
                step="2"
                icon={Bike}
                title="Rider accepts"
                description="A nearby verified rider accepts your request. See their name, photo, plate number, and rating."
              />
              <StepCard
                step="3"
                icon={CheckCircle2}
                title="Track & receive"
                description="Follow your delivery live. Get photo proof and confirmation when it's delivered."
              />
            </div>
          </div>
        </section>

        {/* Riders Section */}
        <section id="riders" className="border-y border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
              <div>
                <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground">
                  Earn with 24Boda
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  Join hundreds of riders earning daily with flexible schedules. Keep 80% of every
                  fare you complete.
                </p>
                <div className="mt-8 space-y-4">
                  <BenefitItem
                    icon={Wallet}
                    title="Daily payouts"
                    description="Get paid every day. No hidden deductions or surprise fees."
                  />
                  <BenefitItem
                    icon={Users}
                    title="Full support"
                    description="24/7 dispatch team, customer support, and rider community."
                  />
                  <BenefitItem
                    icon={ShieldCheck}
                    title="Insurance coverage"
                    description="Protected on every trip. Work with verified customers only."
                  />
                  <BenefitItem
                    icon={Bike}
                    title="Use your own bike"
                    description="Boda, bicycle, or car — bring the vehicle you already have."
                  />
                </div>
                <div className="mt-8">
                  <Button size="lg" variant="default" className="gap-2">
                    Start earning <ArrowRight className="size-4" />
                  </Button>
                </div>
              </div>
              <Card className="p-8">
                <h3 className="text-xl font-semibold text-foreground">Requirements</h3>
                <ul className="mt-6 space-y-4 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <CheckCircle2 className="size-5 shrink-0 text-primary" />
                    <span>Valid national ID and driver's license</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="size-5 shrink-0 text-primary" />
                    <span>Registered vehicle with visible plate number</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="size-5 shrink-0 text-primary" />
                    <span>Smartphone with internet connection</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="size-5 shrink-0 text-primary" />
                    <span>18 years or older</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="size-5 shrink-0 text-primary" />
                    <span>Clean background check</span>
                  </li>
                </ul>
                <div className="mt-8 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <p className="text-sm text-foreground">
                    <strong>Quick approval:</strong> Most riders get approved within 24 hours.
                    Register at our Kampala office with your documents.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="text-center">
              <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground">
                Get in touch
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Business deliveries, bulk orders, or rider onboarding — our team is here to help.
              </p>
            </div>
            <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-3">
              <ContactCard
                icon={Phone}
                title="Phone"
                value="+256 700 000 000"
                subtitle="Available 24/7"
              />
              <ContactCard
                icon={Mail}
                title="Email"
                value="hello@24boda.com"
                subtitle="Response within 24hrs"
              />
              <ContactCard
                icon={Building2}
                title="Office"
                value="Kampala, Uganda"
                subtitle="Walk-ins welcome"
              />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/20 py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center">
              <img src="/logo.png" alt="24Boda" className="h-8 w-auto" />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              © {new Date().getFullYear()} 24Boda. All rights reserved. Kampala, Uganda.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <dt className="text-xl font-bold text-foreground">{value}</dt>
      <dd className="mt-1 text-xs text-muted-foreground">{label}</dd>
    </div>
  );
}

function Feature({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <span className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-4" />
      </span>
      <h3 className="mt-4 text-sm font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function ValueCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card className="group relative overflow-hidden p-6 transition-all hover:shadow-lg">
      <div className="absolute -right-6 -top-6 size-24 rounded-full bg-primary/5 transition-all group-hover:scale-110"></div>
      <div className="relative">
        <div className="inline-flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-6" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </div>
    </Card>
  );
}

function StepCard({
  step,
  icon: Icon,
  title,
  description,
}: {
  step: string;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="relative">
      <div className="flex items-start gap-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
          {step}
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Icon className="size-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}

function BenefitItem({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-5" />
      </div>
      <div>
        <h4 className="font-semibold text-foreground">{title}</h4>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function ContactCard({
  icon: Icon,
  title,
  value,
  subtitle,
}: {
  icon: LucideIcon;
  title: string;
  value: string;
  subtitle: string;
}) {
  return (
    <Card className="p-6 text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="size-6" />
      </div>
      <h3 className="mt-4 text-sm font-medium text-muted-foreground">{title}</h3>
      <p className="mt-2 text-lg font-semibold text-foreground">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
    </Card>
  );
}
